import { useState, useEffect, useCallback, useRef } from "react";
import { useApi } from "../../shared/providers/ApiProvider";
import { ApiResponse } from "../../shared/types";
import {
  DocumentDto,
  GetDocumentsRequest,
  GetDocumentsResponse,
  AddDocumentRequest,
  AddDocumentResponse,
  EditDocumentRequest,
  EditDocumentResponse,
  DeleteDocumentRequest,
  DeleteDocumentResponse,
  DocumentFilterDto,
  DocumentSortFieldDto,
  SortDirectionDto,
  PageDto
} from "../types";
import { useToast } from "../../shared/ui/hooks/use-toast";
import {
  API_ENDPOINTS,
  TOAST_MESSAGES,
  PAGINATION_DEFAULTS
} from "../constants";
import { useDocumentLinkContext } from "../providers/DocumentLinkContext";

interface DocumentApiResponse {
  id: string;
  projectId: number;
  customerId: number;
  fileName: string;
  displayName?: string;
  category?: string;
  mimeType?: string;
  sizeBytes?: number;
  visibilityStatusId: number;
  visibilityStatus: string;
  uploadedUtc: string;
  updatedUtc?: string;
  uploadNotes?: string;
  publicUrl?: string;
}

function toDocumentDto(apiResponse: DocumentApiResponse): DocumentDto {
  return {
    documentId: apiResponse.id,
    projectId: apiResponse.projectId,
    customerId: apiResponse.customerId,
    filename: apiResponse.displayName || apiResponse.fileName,
    fileSize: apiResponse.sizeBytes,
    mimeType: apiResponse.mimeType,
    isPublic: apiResponse.visibilityStatusId === 1,
    dateAdded: apiResponse.uploadedUtc,
    lastModified: apiResponse.updatedUtc,
    comments: apiResponse.uploadNotes,
    downloadUrl: apiResponse.publicUrl,
    tags: []
  };
}

// Helper: normalize API response (handles both axios-style and plain returns)
const normalizeApiResponse = <T,>(resp: any): T | null => {
  if (!resp) return null;
  return resp?.data ?? resp;
};

// Helper: check if two documents are the same file (by name, size, and mime type)
const isSameFile = (
  a: DocumentDto,
  b: { filename: string; fileSize?: number; mimeType?: string }
): boolean => {
  // Heuristic: name + size (and mime if available)
  const sizeEq =
    a.fileSize != null && b.fileSize != null ? a.fileSize === b.fileSize : true;
  const mimeEq = a.mimeType && b.mimeType ? a.mimeType === b.mimeType : true;
  return a.filename === b.filename && sizeEq && mimeEq;
};

export interface UseDocumentsReturn {
  documents: DocumentDto[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  sortField?: DocumentSortFieldDto;
  sortDirection?: SortDirectionDto;
  reload: () => void;
  clearError: () => void;
  updateFilters: (filters: Partial<GetDocumentsRequest>) => void;
  addDocument: (
    documentData: AddDocumentRequest
  ) => Promise<AddDocumentResponse>;
  editDocument: (
    documentData: EditDocumentRequest
  ) => Promise<EditDocumentResponse>;
  deleteDocument: (documentId: string) => Promise<DeleteDocumentResponse>;
  getDownloadUrl: (documentId: string) => Promise<{ url: string } | null>;
}

interface UseDocumentsConfig {
  initialFilters?: Partial<GetDocumentsRequest>;
}

const defaultFilters: Omit<GetDocumentsRequest, "linkType" | "linkTargetId"> = {
  filter: DocumentFilterDto.All,
  searchTerm: "",
  sortField: DocumentSortFieldDto.DateAdded,
  sortDirection: SortDirectionDto.Desc,
  page: 1,
  pageSize: PAGINATION_DEFAULTS.PAGE_SIZE
};

export function useDocuments({
  initialFilters = {}
}: UseDocumentsConfig = {}): UseDocumentsReturn {
  const { linkType, linkTargetId } = useDocumentLinkContext();
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<GetDocumentsRequest>({
    ...defaultFilters,
    linkType,
    linkTargetId,
    ...initialFilters
  });

  const { get, post, put, del } = useApi();
  const { toast } = useToast();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = {
        searchTerm: filters.searchTerm || undefined,
        filter: filters.filter || undefined,
        sortField: filters.sortField || undefined,
        sortDirection: filters.sortDirection || undefined,
        page: filters.page || 1,
        pageSize: filters.pageSize || PAGINATION_DEFAULTS.PAGE_SIZE
      };

      const response = await get<PageDto<DocumentApiResponse>>(
        API_ENDPOINTS.DOCUMENTS,
        { ...queryParams, linkType, linkTargetId }
      );

      if (isMounted.current) {
        if (response.success) {
          const pageData = response.data;
          const documents = (pageData.data || []).map(toDocumentDto);
          setDocuments(documents);
          setTotalCount(pageData.total || 0);
        } else {
          setError(
            response.errors?.description ||
              response.message ||
              "Failed to load documents"
          );
          setDocuments([]);
          setTotalCount(0);
        }
      }
    } catch (err: any) {
      if (isMounted.current) {
        const errorMessage =
          err.errors?.description ||
          (err instanceof Error
            ? err.message
            : "An error occurred while loading documents");
        console.error("Error loading documents:", err);
        setError(errorMessage);
        setDocuments([]);
        setTotalCount(0);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [linkType, linkTargetId, filters, get]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const updateFilters = useCallback(
    (newFilters: Partial<GetDocumentsRequest>) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    []
  );

  const addDocument = useCallback(
    async (documentData: AddDocumentRequest): Promise<AddDocumentResponse> => {
      // 1) Client-side quick dupe guard (prevents obvious double-clicks / repeats)
      // Use the actual file's name, size, and type for comparison
      const pendingCandidate = {
        filename: documentData.file.name, // Use file.name, not the user-entered filename
        fileSize: documentData.file.size,
        mimeType: documentData.file.type
      };
      const alreadyHere = documents.some((d) =>
        isSameFile(d, pendingCandidate)
      );
      if (alreadyHere) {
        toast({
          title: "Duplicate file",
          description: "A file with the same name/size is already in the list.",
          variant: "warning"
        });
        return { success: false, message: "Duplicate in current list" };
      }

      // 2) Build the payload
      const formData = new FormData();
      formData.append("file", documentData.file);
      formData.append("filename", documentData.filename);
      formData.append("isPublic", documentData.isPublic.toString());
      if (documentData.comments) {
        formData.append("comments", documentData.comments);
      }
      if (documentData.tags) {
        formData.append("tags", JSON.stringify(documentData.tags));
      }

      formData.append("linkType", linkType);
      formData.append("linkTargetId", linkTargetId.toString());

      const response = await post<DocumentApiResponse>(
        API_ENDPOINTS.DOCUMENTS,
        formData
      );

      // 4) Check response
      if (!response.success) {
        const errorMsg =
          response.errors?.description ||
          response.message ||
          "Failed to upload document";
        toast({
          title: "Error",
          description: errorMsg,
          variant: "destructive"
        });
        return { success: false, message: errorMsg };
      }

      // 5) Convert to DTO
      const newDocument = toDocumentDto(response.data);

      // 6) Instead of manual prepend, re-sync with server
      //    This eliminates "ghost adds" and stale details after dedupe handling.
      await loadDocuments();

      toast({
        title: "Document uploaded",
        description: "The document has been successfully uploaded.",
        variant: "default"
      });

      return { success: true, data: newDocument };
    },
    [linkType, linkTargetId, post, toast, documents, loadDocuments]
  );

  const editDocument = useCallback(
    async (
      documentData: EditDocumentRequest
    ): Promise<EditDocumentResponse> => {
      const requestBody = {
        documentId: documentData.documentId,
        filename: documentData.filename,
        isPublic: documentData.isPublic,
        comments: documentData.comments,
        tags: documentData.tags ? JSON.stringify(documentData.tags) : undefined
      };

      const response = await put<DocumentApiResponse>(
        API_ENDPOINTS.DOCUMENTS,
        requestBody
      );

      if (response.success) {
        const updatedDocument = toDocumentDto(response.data);

        if (isMounted.current) {
          setDocuments((prev) =>
            prev.map((doc) =>
              doc.documentId === updatedDocument.documentId
                ? updatedDocument
                : doc
            )
          );
        }

        toast({
          title: "Document updated",
          description: "The document has been successfully updated.",
          variant: "default"
        });

        return { success: true, data: updatedDocument };
      } else {
        const errorMsg =
          response.errors?.description ||
          response.message ||
          "Failed to update document";
        toast({
          title: "Error",
          description: errorMsg,
          variant: "destructive"
        });
        return { success: false, message: errorMsg };
      }
    },
    [put, toast, isMounted]
  );

  const deleteDocument = useCallback(
    async (documentId: string): Promise<DeleteDocumentResponse> => {
      const response = await del<void>(API_ENDPOINTS.DOCUMENTS, { documentId });

      if (response.success) {
        if (isMounted.current) {
          setDocuments((prev) =>
            prev.filter((doc) => doc.documentId !== documentId)
          );
          setTotalCount((prev) => prev - 1);
        }

        toast({
          title: "Document deleted",
          description: "The document has been successfully deleted.",
          variant: "default"
        });

        return { success: true };
      } else {
        const errorMsg =
          response.errors?.description ||
          response.message ||
          "Failed to delete document";
        toast({
          title: "Error",
          description: errorMsg,
          variant: "destructive"
        });
        return { success: false, message: errorMsg };
      }
    },
    [del, toast, isMounted]
  );

  const getDownloadUrl = useCallback(
    async (documentId: string): Promise<{ url: string } | null> => {
      const response = await get<{ url: string }>(
        API_ENDPOINTS.DOCUMENTS_DOWNLOAD_URL,
        { documentId }
      );

      if (response.success && response.data.url) {
        return { url: response.data.url };
      }

      const errorMsg =
        response.errors?.description ||
        response.message ||
        "Failed to get download URL";
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
      return null;
    },
    [get, toast]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const totalPages = Math.ceil(totalCount / (filters.pageSize || 25));

  return {
    documents,
    loading,
    error,
    totalCount,
    page: filters.page || 1,
    pageSize: filters.pageSize || 25,
    totalPages,
    sortField: filters.sortField,
    sortDirection: filters.sortDirection,
    reload: loadDocuments,
    clearError,
    updateFilters,
    addDocument,
    editDocument,
    deleteDocument,
    getDownloadUrl
  };
}
